// Calendar *data* (month/weekday names, first day of week, display format, range keywords) for
// the three locales the design specifies. This is locale DATA — not app copy — so it lives inline
// (app i18n only carries cs/en, and pl month names must ship regardless). UI action labels and
// error messages are separate and go through i18n (`i18nManifest.ts`).

import { pad2 } from './dateCore';

export type DateFieldLocale = 'cs' | 'en' | 'pl';

export interface DateLocaleData {
  code: DateFieldLocale;
  /** Full month names, index 0 = January. */
  months: readonly string[];
  /** Short month names for the wheel column. */
  monthsShort: readonly string[];
  /** Weekday headers already ordered from `firstDay`. */
  weekdays: readonly string[];
  /** First day of week: 1 = Monday (cs/pl), 0 = Sunday (en). */
  firstDay: 0 | 1;
  /** Range keyword shown/parsed for an open end ("od" / "from"). */
  rangeFrom: string;
  /** Range keyword shown/parsed for an open start ("do" / "until"). */
  rangeTo: string;
  /** Placeholder for a single value. */
  placeholder: string;
  /** Placeholder for a range value. */
  placeholderRange: string;
  /** Format a `Date` for display. */
  format: (date: Date) => string;
}

const cs: DateLocaleData = {
  code: 'cs',
  months: [
    'leden',
    'únor',
    'březen',
    'duben',
    'květen',
    'červen',
    'červenec',
    'srpen',
    'září',
    'říjen',
    'listopad',
    'prosinec',
  ],
  monthsShort: ['led', 'úno', 'bře', 'dub', 'kvě', 'čvn', 'čvc', 'srp', 'zář', 'říj', 'lis', 'pro'],
  weekdays: ['po', 'út', 'st', 'čt', 'pá', 'so', 'ne'],
  firstDay: 1,
  rangeFrom: 'od',
  rangeTo: 'do',
  placeholder: 'd. m. rrrr',
  placeholderRange: 'd.m.rrrr – d.m.rrrr · od / do',
  format: (d) => `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`,
};

const en: DateLocaleData = {
  code: 'en',
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  weekdays: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  firstDay: 0,
  rangeFrom: 'from',
  rangeTo: 'until',
  placeholder: 'M/D/YYYY',
  placeholderRange: 'M/D/YYYY – M/D/YYYY · from / until',
  format: (d) => `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`,
};

const pl: DateLocaleData = {
  code: 'pl',
  months: [
    'styczeń',
    'luty',
    'marzec',
    'kwiecień',
    'maj',
    'czerwiec',
    'lipiec',
    'sierpień',
    'wrzesień',
    'październik',
    'listopad',
    'grudzień',
  ],
  monthsShort: ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'],
  weekdays: ['pn', 'wt', 'śr', 'cz', 'pt', 'so', 'nd'],
  firstDay: 1,
  rangeFrom: 'od',
  rangeTo: 'do',
  placeholder: 'd.mm.rrrr',
  placeholderRange: 'd.mm.rrrr – d.mm.rrrr · od / do',
  format: (d) => `${d.getDate()}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`,
};

const LOCALES: Record<DateFieldLocale, DateLocaleData> = { cs, en, pl };

/** Resolve calendar data for a locale, falling back to `cs`. */
export function getDateLocale(locale: DateFieldLocale | undefined): DateLocaleData {
  return LOCALES[locale ?? 'cs'] ?? cs;
}

/** Map an i18n language tag (e.g. `"en-GB"`) onto a supported calendar locale. */
export function resolveDateLocale(language: string | undefined): DateFieldLocale {
  const base = (language ?? '').slice(0, 2).toLowerCase();
  return base === 'en' || base === 'pl' ? base : 'cs';
}
